import { useState, useEffect } from 'react'
import { FileSpreadsheet, Search, Download, Printer, Loader2 } from 'lucide-react'
import api from '../../core/services/api'

interface TemplateItem {
  id: string
  codice: string
  nome: string
  descrizione: string | null
  is_riepilogabile: boolean
}

interface EntitaItem {
  id: string
  label: string
}

interface CampoItem {
  name: string
  label: string
  type: string
  sommabile: boolean
}

interface ColonnaRiepilogo {
  name: string
  label: string
  type: string
}

interface AnteprimaData {
  titolo_template: string
  entita_label: string
  periodo: string
  colonne: ColonnaRiepilogo[]
  righe: Record<string, any>[]
  totali: Record<string, number>
  conteggio: number
}

interface TemplateDocItem {
  id: string
  nome: string
  tipo: string
}

export default function RiepiloghiPage() {
  // Step 1: Selezione template
  const [templates, setTemplates] = useState<TemplateItem[]>([])
  const [selectedTemplateId, setSelectedTemplateId] = useState('')
  const [loadingTemplates, setLoadingTemplates] = useState(true)

  // Step 2: Tipo e entita'
  const [parentType, setParentType] = useState<'commessa' | 'fascicolo' | 'strumento'>('commessa')
  const [searchEntita, setSearchEntita] = useState('')
  const [entitaResults, setEntitaResults] = useState<EntitaItem[]>([])
  const [selectedEntita, setSelectedEntita] = useState<EntitaItem | null>(null)
  const [loadingEntita, setLoadingEntita] = useState(false)

  // Step 3: Periodo
  const [dataDa, setDataDa] = useState('')
  const [dataA, setDataA] = useState('')

  // Step 4: Campi
  const [campi, setCampi] = useState<CampoItem[]>([])
  const [campiInclusi, setCampiInclusi] = useState<Set<string>>(new Set())
  const [campiSomma, setCampiSomma] = useState<Set<string>>(new Set())
  const [loadingCampi, setLoadingCampi] = useState(false)

  // Step 5: Anteprima
  const [anteprima, setAnteprima] = useState<AnteprimaData | null>(null)
  const [loadingAnteprima, setLoadingAnteprima] = useState(false)

  // Genera doc
  const [templatesDocs, setTemplatesDocs] = useState<TemplateDocItem[]>([])
  const [selectedDocTemplateId, setSelectedDocTemplateId] = useState('')
  const [generatingDoc, setGeneratingDoc] = useState(false)

  // Date di default: primo e ultimo giorno del mese corrente
  useEffect(() => {
    const now = new Date()
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1)
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0)
    setDataDa(firstDay.toISOString().split('T')[0])
    setDataA(lastDay.toISOString().split('T')[0])
  }, [])

  // Carica template riepilogabili
  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get('/v1/schede-dinamiche/templates', { params: { is_active: true } })
        const riepilogabili = (res.data as TemplateItem[]).filter(t => t.is_riepilogabile !== false)
        setTemplates(riepilogabili)
      } catch (e) {
        console.error('Errore caricamento templates:', e)
      } finally {
        setLoadingTemplates(false)
      }
    }
    load()
  }, [])

  // Ricerca entita' (debounced)
  useEffect(() => {
    if (!searchEntita.trim() || searchEntita.length < 2) {
      setEntitaResults([])
      return
    }
    const timer = setTimeout(async () => {
      setLoadingEntita(true)
      try {
        const res = await api.get('/v1/schede-dinamiche/riepilogo/ricerca-entita', {
          params: { tipo: parentType, search: searchEntita }
        })
        setEntitaResults(res.data)
      } catch (e) {
        console.error('Errore ricerca entita:', e)
      } finally {
        setLoadingEntita(false)
      }
    }, 300)
    return () => clearTimeout(timer)
  }, [searchEntita, parentType])

  // Carica campi quando il template cambia
  useEffect(() => {
    if (!selectedTemplateId) {
      setCampi([])
      return
    }
    const load = async () => {
      setLoadingCampi(true)
      try {
        const res = await api.get(`/v1/schede-dinamiche/riepilogo/campi/${selectedTemplateId}`)
        setCampi(res.data)
        // Seleziona tutti i campi di default
        const allNames = new Set((res.data as CampoItem[]).map(c => c.name))
        setCampiInclusi(allNames)
        setCampiSomma(new Set())
      } catch (e) {
        console.error('Errore caricamento campi:', e)
      } finally {
        setLoadingCampi(false)
      }
    }
    load()
  }, [selectedTemplateId])

  // Carica template documenti tipo riepilogo
  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get('/v1/template-documenti', { params: { tipo: 'riepilogo' } })
        setTemplatesDocs(res.data)
        if (res.data.length > 0) {
          setSelectedDocTemplateId(res.data[0].id)
        }
      } catch {
        // Non critico
      }
    }
    load()
  }, [])

  const toggleCampo = (name: string) => {
    setCampiInclusi(prev => {
      const next = new Set(prev)
      if (next.has(name)) {
        next.delete(name)
        // Rimuovi anche da somma
        setCampiSomma(sp => {
          const ns = new Set(sp)
          ns.delete(name)
          return ns
        })
      } else {
        next.add(name)
      }
      return next
    })
  }

  const toggleSomma = (name: string) => {
    setCampiSomma(prev => {
      const next = new Set(prev)
      if (next.has(name)) next.delete(name)
      else next.add(name)
      return next
    })
  }

  const canSearch = selectedTemplateId && selectedEntita && dataDa && dataA && campiInclusi.size > 0

  const handleAnteprima = async () => {
    if (!canSearch) return
    setLoadingAnteprima(true)
    setAnteprima(null)
    try {
      const res = await api.post('/v1/schede-dinamiche/riepilogo/anteprima', {
        template_id: selectedTemplateId,
        parent_entity_type: parentType,
        parent_entity_id: selectedEntita!.id,
        data_da: dataDa,
        data_a: dataA,
        campi_inclusi: Array.from(campiInclusi),
        campi_somma: Array.from(campiSomma),
      })
      setAnteprima(res.data)
    } catch (e: any) {
      console.error('Errore anteprima:', e)
      alert(e?.response?.data?.detail || 'Errore generazione anteprima')
    } finally {
      setLoadingAnteprima(false)
    }
  }

  const handleStampa = () => {
    window.print()
  }

  const handleGeneraDocumento = async () => {
    if (!selectedDocTemplateId || !anteprima) return
    setGeneratingDoc(true)
    try {
      const res = await api.post('/v1/schede-dinamiche/riepilogo/genera-documento', {
        template_id: selectedTemplateId,
        parent_entity_type: parentType,
        parent_entity_id: selectedEntita!.id,
        data_da: dataDa,
        data_a: dataA,
        campi_inclusi: Array.from(campiInclusi),
        campi_somma: Array.from(campiSomma),
        template_documento_id: selectedDocTemplateId,
      }, { responseType: 'blob' })

      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `Riepilogo_${dataDa}_${dataA}.docx`
      a.click()
      URL.revokeObjectURL(url)
    } catch (e: any) {
      console.error('Errore genera documento:', e)
      alert('Errore nella generazione del documento')
    } finally {
      setGeneratingDoc(false)
    }
  }

  const formatValue = (val: any, type: string) => {
    if (val === null || val === undefined || val === '') return '-'
    if (type === 'date' && typeof val === 'string') {
      try {
        const d = new Date(val)
        if (!isNaN(d.getTime())) return d.toLocaleDateString('it-IT')
      } catch { /* */ }
    }
    if (type === 'currency' && typeof val === 'number') {
      return val.toLocaleString('it-IT', { style: 'currency', currency: 'EUR' })
    }
    if (type === 'boolean') return val ? 'Si' : 'No'
    return String(val)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <FileSpreadsheet className="w-7 h-7 text-primary-600" />
        <h1 className="text-2xl font-bold text-gray-900">Riepiloghi Attivita</h1>
      </div>

      {/* Wizard */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-6 print:hidden">

        {/* Step 1: Template */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">
            1. Tipo scheda
          </label>
          {loadingTemplates ? (
            <div className="flex items-center gap-2 text-gray-400 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Caricamento...</div>
          ) : (
            <select
              value={selectedTemplateId}
              onChange={e => {
                setSelectedTemplateId(e.target.value)
                setAnteprima(null)
              }}
              className="w-full md:w-96 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">-- Seleziona template --</option>
              {templates.map(t => (
                <option key={t.id} value={t.id}>{t.nome}</option>
              ))}
            </select>
          )}
        </div>

        {/* Step 2: Tipo entita' */}
        {selectedTemplateId && (
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              2. Tipo entita
            </label>
            <div className="flex gap-4 mb-3">
              {(['commessa', 'fascicolo', 'strumento'] as const).map(tipo => (
                <label key={tipo} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="parentType"
                    value={tipo}
                    checked={parentType === tipo}
                    onChange={() => {
                      setParentType(tipo)
                      setSelectedEntita(null)
                      setSearchEntita('')
                      setEntitaResults([])
                      setAnteprima(null)
                    }}
                    className="text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm text-gray-700 capitalize">{tipo}</span>
                </label>
              ))}
            </div>

            {/* Ricerca entita' */}
            <div className="relative w-full md:w-96">
              <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder={`Cerca ${parentType} per nome o numero...`}
                value={selectedEntita ? selectedEntita.label : searchEntita}
                onChange={e => {
                  setSearchEntita(e.target.value)
                  if (selectedEntita) {
                    setSelectedEntita(null)
                    setAnteprima(null)
                  }
                }}
                onFocus={() => {
                  if (selectedEntita) {
                    setSearchEntita(selectedEntita.label)
                    setSelectedEntita(null)
                  }
                }}
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
              {loadingEntita && (
                <Loader2 className="absolute right-3 top-2.5 w-4 h-4 text-gray-400 animate-spin" />
              )}
              {/* Dropdown risultati */}
              {entitaResults.length > 0 && !selectedEntita && (
                <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  {entitaResults.map(e => (
                    <button
                      key={e.id}
                      onClick={() => {
                        setSelectedEntita(e)
                        setSearchEntita('')
                        setEntitaResults([])
                        setAnteprima(null)
                      }}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-primary-50 hover:text-primary-700"
                    >
                      {e.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {selectedEntita && (
              <p className="text-xs text-green-600 mt-1">Selezionato: {selectedEntita.label}</p>
            )}
          </div>
        )}

        {/* Step 3: Periodo */}
        {selectedTemplateId && selectedEntita && (
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              3. Periodo
            </label>
            <div className="flex items-center gap-3">
              <input
                type="date"
                value={dataDa}
                onChange={e => { setDataDa(e.target.value); setAnteprima(null) }}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500"
              />
              <span className="text-gray-500">-</span>
              <input
                type="date"
                value={dataA}
                onChange={e => { setDataA(e.target.value); setAnteprima(null) }}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>
        )}

        {/* Step 4: Campi */}
        {selectedTemplateId && selectedEntita && campi.length > 0 && (
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              4. Campi da includere
            </label>
            {loadingCampi ? (
              <div className="flex items-center gap-2 text-gray-400 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Caricamento...</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                {campi.map(c => (
                  <div key={c.name} className="flex items-center gap-3 bg-gray-50 rounded-lg px-3 py-2">
                    <input
                      type="checkbox"
                      checked={campiInclusi.has(c.name)}
                      onChange={() => toggleCampo(c.name)}
                      className="text-primary-600 focus:ring-primary-500 rounded"
                    />
                    <span className="text-sm text-gray-700 flex-1">{c.label}</span>
                    {c.sommabile && campiInclusi.has(c.name) && (
                      <label className="flex items-center gap-1 text-xs text-gray-500 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={campiSomma.has(c.name)}
                          onChange={() => toggleSomma(c.name)}
                          className="text-green-600 focus:ring-green-500 rounded w-3 h-3"
                        />
                        Somma
                      </label>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Bottone genera anteprima */}
        {canSearch && (
          <div>
            <button
              onClick={handleAnteprima}
              disabled={loadingAnteprima}
              className="px-6 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2 font-medium text-sm"
            >
              {loadingAnteprima ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Ricerca in corso...</>
              ) : (
                <><Search className="w-4 h-4" /> Genera anteprima</>
              )}
            </button>
          </div>
        )}
      </div>

      {/* Anteprima tabella */}
      {anteprima && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" id="riepilogo-print">
          <div className="flex items-center justify-between mb-4 print:mb-6">
            <div>
              <h2 className="text-lg font-bold text-gray-900">{anteprima.titolo_template}</h2>
              <p className="text-sm text-gray-600">{anteprima.entita_label}</p>
              <p className="text-sm text-gray-500">Periodo: {anteprima.periodo}</p>
              <p className="text-sm text-gray-500">Schede trovate: {anteprima.conteggio}</p>
            </div>
            <div className="flex gap-2 print:hidden">
              <button
                onClick={handleStampa}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center gap-2 text-sm"
              >
                <Printer className="w-4 h-4" /> Stampa
              </button>
              {templatesDocs.length > 0 && (
                <div className="flex items-center gap-2">
                  {templatesDocs.length > 1 && (
                    <select
                      value={selectedDocTemplateId}
                      onChange={e => setSelectedDocTemplateId(e.target.value)}
                      className="border border-gray-300 rounded-lg px-2 py-2 text-sm"
                    >
                      {templatesDocs.map(t => (
                        <option key={t.id} value={t.id}>{t.nome}</option>
                      ))}
                    </select>
                  )}
                  <button
                    onClick={handleGeneraDocumento}
                    disabled={generatingDoc}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2 text-sm"
                  >
                    {generatingDoc ? (
                      <><Loader2 className="w-4 h-4 animate-spin" /> Generazione...</>
                    ) : (
                      <><Download className="w-4 h-4" /> Genera documento</>
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>

          {anteprima.conteggio === 0 ? (
            <p className="text-gray-500 text-center py-8">Nessuna scheda compilata trovata nel periodo selezionato.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="text-left px-3 py-2 border border-gray-200 text-xs font-semibold text-gray-600">#</th>
                    {anteprima.colonne.map(col => (
                      <th key={col.name} className="text-left px-3 py-2 border border-gray-200 text-xs font-semibold text-gray-600">
                        {col.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {anteprima.righe.map((riga, idx) => (
                    <tr key={riga._istanza_id || idx} className="hover:bg-gray-50">
                      <td className="px-3 py-1.5 border border-gray-200 text-gray-400 text-xs">{idx + 1}</td>
                      {anteprima.colonne.map(col => (
                        <td key={col.name} className="px-3 py-1.5 border border-gray-200 text-gray-800">
                          {formatValue(riga[col.name], col.type)}
                        </td>
                      ))}
                    </tr>
                  ))}
                  {/* Riga totali */}
                  {Object.keys(anteprima.totali).length > 0 && (
                    <tr className="bg-gray-100 font-semibold">
                      <td className="px-3 py-2 border border-gray-200 text-gray-600 text-xs">TOT</td>
                      {anteprima.colonne.map(col => (
                        <td key={col.name} className="px-3 py-2 border border-gray-200 text-gray-900">
                          {col.name in anteprima.totali
                            ? formatValue(anteprima.totali[col.name], col.type)
                            : ''
                          }
                        </td>
                      ))}
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* CSS stampa */}
      <style>{`
        @media print {
          body * { visibility: hidden; }
          #riepilogo-print, #riepilogo-print * { visibility: visible; }
          #riepilogo-print { position: absolute; left: 0; top: 0; width: 100%; }
        }
      `}</style>
    </div>
  )
}
